import {
    AnalyticsCTAEvent,
    useTrackCTAClickedAnalytics,
} from '@/utils/amplitude/analytics/cta/trackCTAClicked.analytics';
import { BidCanvas } from '@liveauctioneers/hammer-ui-liveauctioneers/lotDetail';
import { MaxBidSelector } from '@/components/ItemBidding/components/BidCanvasContainer/components/MaxBidSelector';
import { openCompleteAccountSignUpModal } from '@/redux/modules/modal';
import { temporaryBidderMaxBidAtom } from '@/components/Modals/AuthModal/AbsenteeBid/pages/AbsenteeBid/components/PlaceBidForm/components/SelectBidDropdown/SelectBidDropdown';
import { TrackPlaceBidPressedButtonType } from '@/utils/amplitude/analytics';
import { useAppDispatch } from '@/redux/hooks';
import { useBuyNowCallbacks } from './hooks/useHandleBidCanvasCallbacks/useBuyNowCallbacks';
import { useCallback } from 'react';
import { useEnterLiveAuction } from './hooks/useHandleBidCanvasCallbacks/useEnterLiveAuction';
import { useGetBidCanvasBuyerData } from './hooks/useGetBidCanvasBuyerData';
import { useGetBidCanvasCatalogData } from './hooks/useGetBidCanvasCatalogData';
import { useGetBidCanvasItemData } from './hooks/useGetBidCanvasItemData';
import { useGetBidCanvasZipData } from './hooks/useGetBidCanvasZipData';
import { useHandleLinkClicked } from './hooks/useHandleBidCanvasCallbacks/useHandleLinkClicked';
import { useOpenZipPayModal } from './hooks/useHandleBidCanvasCallbacks/useOpenZipPayModal';
import { usePlaceBid } from './hooks/useHandleBidCanvasCallbacks/usePlaceBid';
import { useRegisterForAuction } from './hooks/useHandleBidCanvasCallbacks/useRegisterForAuction';
import { useRemoveZipPaymentInfoExperiment } from '@/utils/amplitude/experiments/hooks/useRemoveZipPaymentInfo';
import { useReservePriceStatusExperiment } from '@/hooks/kameleoon/ReservePriceStatus/useReservePriceStatusExperiment';
import { useSeeSoldPrice } from './hooks/useHandleBidCanvasCallbacks/useSeeSoldPrice';
import { useSendSellerMessage } from './hooks/useHandleBidCanvasCallbacks/useSendSellerMessage';
import { useSetAtom } from 'jotai';
import { useViewOrRegisterOnPartnerSite } from './hooks/useHandleBidCanvasCallbacks/useViewOrRegisterOnPartnerSite';

type Props = {
    catalogId: number;
    className?: string;
    itemId: number;
    onCountdownEnd: () => void;
};

export const BidCanvasContainer = ({ catalogId, className, itemId, onCountdownEnd }: Props) => {
    const dispatch = useAppDispatch();
    const zipPay = useGetBidCanvasZipData({ itemId });
    const buyer = useGetBidCanvasBuyerData({ itemId });
    const catalog = useGetBidCanvasCatalogData({ itemId });
    const item = useGetBidCanvasItemData({ itemId, onCountdownEnd });
    const hasReservePriceStatus = useReservePriceStatusExperiment();
    const trackCTAClicked = useTrackCTAClickedAnalytics();

    const hasRemoveZipPaymentInformationTreatment = useRemoveZipPaymentInfoExperiment();
    const setTemporaryBidderMaxBidAtom = useSetAtom(temporaryBidderMaxBidAtom);

    // CALLBACKS
    const onPlaceBidClicked = usePlaceBid({ itemId });
    const onEnterLiveAuctionClicked = useEnterLiveAuction({ catalogId });
    const onLinkClickedFunction = useHandleLinkClicked({ itemId });
    const onZipInfoClicked = useOpenZipPayModal();
    const onRegisterForAuctionClicked = useRegisterForAuction({ catalogId, itemId });
    const onSeeSoldPriceClicked = useSeeSoldPrice({ itemId });
    const onSendMessageClicked = useSendSellerMessage({ itemId });
    const onRegisterOnPartnerSiteClickedFunction = useViewOrRegisterOnPartnerSite({ catalogId, itemId });
    const { onBuyNowClicked, onMakeAnOfferClicked, onViewMoreBuyItNowItemsClicked } = useBuyNowCallbacks({ itemId });

    const onRegisterOnPartnerSiteClicked = useCallback(() => {
        onRegisterOnPartnerSiteClickedFunction('register');
    }, [onRegisterOnPartnerSiteClickedFunction]);

    const onViewOnPartnerSiteClicked = useCallback(() => {
        onRegisterOnPartnerSiteClickedFunction('view');
    }, [onRegisterOnPartnerSiteClickedFunction]);

    const onSellSimilarItemClicked = useCallback(() => {
        onLinkClickedFunction('sell_similar_item');
    }, [onLinkClickedFunction]);

    const onTipsForGettingApprovedClicked = useCallback(() => {
        onLinkClickedFunction('tips_for_getting_approved');
    }, [onLinkClickedFunction]);

    const onTipsForWinningClicked = useCallback(() => {
        onLinkClickedFunction('tips_for_winning');
    }, [onLinkClickedFunction]);

    const onCompleteProfileClicked = useCallback(() => {
        dispatch(openCompleteAccountSignUpModal());
    }, [dispatch]);

    const wrappedRegisterForAuctionClick = useCallback(() => {
        // Track the CTA click
        trackCTAClicked(AnalyticsCTAEvent.ItemPage.RegisterForAuction);

        // Then call the original handler
        onRegisterForAuctionClicked();
    }, [onRegisterForAuctionClicked, trackCTAClicked]);

    const onBidPanelChipIncrementsClicked = useCallback(
        (bidAmount: number, buttonType?: TrackPlaceBidPressedButtonType) => {
            setTemporaryBidderMaxBidAtom(bidAmount);
            onPlaceBidClicked(buttonType);
        },
        [onPlaceBidClicked, setTemporaryBidderMaxBidAtom]
    );

    return (
        <div className={className}>
            <BidCanvas
                buyer={buyer}
                callbacks={{
                    onBidPanelChipIncrementsClicked,
                    onBuyNowClicked,
                    onCompleteProfileClicked,
                    onEnterLiveAuctionClicked,
                    onLoginClicked: onRegisterForAuctionClicked,
                    onMakeAnOfferClicked,
                    onPlaceBidClicked: () => {
                        onPlaceBidClicked('Place Bid Button');
                    },
                    onRegisterForAuctionClicked: wrappedRegisterForAuctionClick,
                    onRegisterOnPartnerSiteClicked,
                    onSeeSoldPriceClicked,
                    onSellSimilarItemClicked,
                    onSendMessageClicked,
                    onSignUpClicked: onRegisterForAuctionClicked,
                    onTipsForGettingApprovedClicked,
                    onTipsForWinningClicked,
                    onViewMoreBuyItNowItemsClicked,
                    onViewOnPartnerSiteClicked,
                    onZipInfoClicked,
                }}
                catalog={catalog}
                item={item}
                variations={{
                    bidFieldVariant: {
                        bidFieldMaxBidComponent: <MaxBidSelector itemId={itemId} />,
                        enabled: true,
                    },
                    hasAdvancedStickyMobileBidButton: false,
                    hasAutoApproveWithBidLimitExperiment: false,
                    hasMobileLotNavigation: false,
                    hasRemoveZipPaymentInformationTreatment,
                    hasReservePriceStatusExperiment: hasReservePriceStatus,
                    hasStickyMobileBidButton: false,
                    showNoReservePriceBadge: item.showNoReserve,
                }}
                zipPay={zipPay}
            />
        </div>
    );
};
